import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Megaphone } from "lucide-react";
import { API, adminCall } from "@/utils/api";

const INITIAL = { district: "", point: "", sqft: "", message: "", valid_until: "", deposit_amount: "" };

const AP_DISTRICTS = [
  "Alluri Sitharama Raju", "Anakapalli", "Anantapur", "Annamayya", "Bapatla",
  "Chittoor", "East Godavari", "Eluru", "Guntur", "Kakinada", "Konaseema",
  "Krishna", "Kurnool", "Nandyal", "NTR", "Palnadu", "Parvathipuram Manyam",
  "Prakasam", "Sri Potti Sriramulu Nellore", "Sri Sathya Sai", "Srikakulam",
  "Tirupati", "Visakhapatnam", "Vizianagaram", "West Godavari", "YSR Kadapa",
];

const isExpired = (n) => n.valid_until && n.valid_until < new Date().toISOString().slice(0, 10);

const inputClass =
  "w-full bg-night border border-line px-4 py-3 text-sm text-ink focus:border-brand outline-none font-mono transition-colors";
const labelClass = "block font-mono text-xs uppercase text-mute mb-2";

const AdminNotices = ({ notices, onChanged }) => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleRelease = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminCall(() =>
        axios.post(
          `${API}/admin/notices`,
          {
            ...form,
            valid_until: form.valid_until || null,
            deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
          },
          { withCredentials: true }
        )
      );
      toast.success("Notification saved as draft — approve it to publish.");
      setForm(INITIAL);
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? `Release failed: ${detail}`
          : err.response
            ? `Release failed (server error ${err.response.status}).`
            : "Release failed — no connection. Check your internet and retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminCall(() => axios.delete(`${API}/admin/notices/${id}`, { withCredentials: true }));
      toast.success("Notification withdrawn.");
      onChanged();
    } catch {
      toast.error("Could not withdraw this notification.");
    }
  };

  const handleApprove = async (id) => {
    try {
      await adminCall(() => axios.patch(`${API}/admin/notices/${id}/approve`, {}, { withCredentials: true }));
      toast.success("Notification approved — now live on the site.");
      onChanged();
    } catch {
      toast.error("Could not approve this notification.");
    }
  };

  return (
    <div className="mt-16" data-testid="admin-notices-panel">
      <div className="mb-8 pb-6 border-b border-line">
        <p className="font-mono text-xs uppercase text-brand tracking-widest mb-2">Release Access</p>
        <h2 className="font-display text-[clamp(24px,3vw,40px)] uppercase tracking-wide">
          District-Wise Point Notifications
        </h2>
      </div>

      <form onSubmit={handleRelease} className="bg-surface border border-line p-8 space-y-6 mb-10" data-testid="notice-release-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={labelClass} htmlFor="notice-district">District (Andhra Pradesh)</label>
            <select
              id="notice-district"
              required
              value={form.district}
              onChange={update("district")}
              className={inputClass}
              data-testid="notice-district-input"
            >
              <option value="">Select district...</option>
              {AP_DISTRICTS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="notice-point">Warehouse Point</label>
            <input
              type="text"
              id="notice-point"
              required
              value={form.point}
              onChange={update("point")}
              className={inputClass}
              placeholder="e.g. 1, 2 or Warehouse No. 4, Gajuwaka"
              data-testid="notice-point-input"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="notice-sqft">Sq.Ft (optional)</label>
            <input
              type="text"
              id="notice-sqft"
              value={form.sqft}
              onChange={update("sqft")}
              className={inputClass}
              placeholder="e.g. 25,000 Sq.Ft"
              data-testid="notice-sqft-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className={labelClass} htmlFor="notice-valid-until">Valid Until (optional — auto-withdraws after this date)</label>
            <input
              type="date"
              id="notice-valid-until"
              value={form.valid_until}
              onChange={update("valid_until")}
              min={new Date().toISOString().slice(0, 10)}
              className={inputClass}
              data-testid="notice-valid-until-input"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="notice-deposit">Security Deposit ₹ (optional)</label>
            <input
              type="number"
              id="notice-deposit"
              min="0"
              value={form.deposit_amount}
              onChange={update("deposit_amount")}
              className={inputClass}
              placeholder="e.g. 50000"
              data-testid="notice-deposit-input"
            />
          </div>
        </div>
        <div>
          <label className={labelClass} htmlFor="notice-message">Short Message (optional)</label>
          <textarea
            id="notice-message"
            rows="2"
            value={form.message}
            onChange={update("message")}
            className={inputClass}
            placeholder="e.g. Grade-A facility, dock levelers, available immediately"
            data-testid="notice-message-input"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="flex items-center justify-center gap-2 w-full md:w-auto bg-brand hover:bg-brandhover disabled:opacity-60 text-night font-mono text-xs font-bold uppercase tracking-widest px-8 py-4 transition-colors"
          data-testid="notice-release-btn"
        >
          <Megaphone className="w-4 h-4" /> {submitting ? "Releasing..." : "Release Notification"}
        </button>
      </form>

      {notices.length > 0 && (
        <div className="border border-line" data-testid="admin-notices-list">
          {notices.map((n) => (
            <div
              key={n.id}
              className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-6 py-4 border-b border-line last:border-b-0 bg-surface"
              data-testid={`admin-notice-${n.id}`}
            >
              <div>
                <p className="text-sm font-bold uppercase">
                  <span className="text-brand font-mono text-xs mr-2">{n.district}</span>
                  {n.point}
                  {n.sqft && <span className="font-mono text-[11px] text-mute ml-2">{n.sqft}</span>}
                  <span className={`font-mono text-[10px] uppercase tracking-wider border px-2 py-0.5 ml-2 ${n.status === "live" ? "text-ok border-ok" : "text-warn border-warn"}`} data-testid={`notice-status-${n.id}`}>
                    {n.status === "live" ? "LIVE" : "DRAFT"}
                  </span>
                </p>
                {n.deposit_amount ? (
                  <p className="font-mono text-[11px] text-brand mt-1">
                    Security deposit: ₹{Number(n.deposit_amount).toLocaleString("en-IN")}
                  </p>
                ) : null}
                {n.message && <p className="text-xs text-mute mt-1">{n.message}</p>}
                <p className="font-mono text-[10px] text-mute uppercase tracking-wider mt-1">
                  Released: {new Date(n.released_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  {n.valid_until && (
                    <span className={isExpired(n) ? "text-warn font-bold" : ""}>
                      {" "}· Valid until: {new Date(`${n.valid_until}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      {isExpired(n) && " — EXPIRED (hidden from site)"}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-3 self-start md:self-auto">
                {n.status !== "live" && (
                  <button
                    onClick={() => handleApprove(n.id)}
                    className="flex items-center gap-2 bg-brand hover:bg-brandhover text-night font-mono text-xs font-bold uppercase tracking-wider px-4 py-2 transition-colors"
                    data-testid={`notice-approve-${n.id}`}
                  >
                    Approve →
                  </button>
                )}
                <button
                  onClick={() => handleDelete(n.id)}
                  className="flex items-center gap-2 border border-line hover:border-warn text-mute hover:text-warn font-mono text-xs uppercase tracking-wider px-4 py-2 transition-colors"
                  data-testid={`notice-delete-${n.id}`}
                >
                  <Trash2 className="w-4 h-4" /> Withdraw
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminNotices;
